
import Vue from "vue";
import { Component, ModelSync, Prop, Watch } from "vue-property-decorator";
import Dialog from "@/components/UI/Dialog.vue";
import Icon from "@/components/UI/Icon.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import { Variable, VariableType } from "@/oxygen/variables/lib/types";
import {
  createVariableName,
  DEFAULT_VARIABLES_COLLECTION,
  getInputForVariableType,
  getUniqueNameWithSuffix
} from "@/oxygen/variables/lib/utils";
import { globalStore } from "@/store/store";
import { v4 as uuidv4 } from "uuid";
import { addVariableToStoreAndSaveVariablesToWordPressIfVariablesPanelIsClosed } from "@/oxygen/variables/lib/data";
import NumberInput from "@/components/Reusable/CustomInputs/NumberInput.vue";
import FontFamilyInput from "@/components/Reusable/CustomInputs/FontFamily/FontFamilyInput.vue";
import ColorInput from "@/components/Reusable/CustomInputs/Color/ColorInput.vue";
import UnitInput from "@/components/Reusable/CustomInputs/UnitInput/UnitInput.vue";
import VariableIcon from "@/oxygen/variables/VariableIcon.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import { DEFAULT_OXYGEN_POPOUT_WIDTH } from "@/lib/panels/panels";
import { MediaV, Media } from "@/types/media";
import { pipe } from "fp-ts/function";
import { fold } from "fp-ts/Either";

@Component({
  components: {
    DropdownInput,
    Dropdown,
    VariableIcon,
    UnitInput,
    ColorInput,
    FontFamilyInput,
    NumberInput,
    TextInput,
    Icon,
    Dialog
  }
})
export default class SaveVariableDropdown extends Vue {
  @ModelSync("open", "toggle")
  isOpen?: boolean;

  @Prop()
  name!: string;

  @Prop()
  type!: VariableType;

  @Prop()
  path!: string;

  @Prop()
  value!: unknown;

  variableLabel = "";
  variableValue: unknown = null;
  variableCollection = "";

  @Watch("isOpen")
  onOpenChange() {
    if (this.isOpen) {
      this.variableLabel = this.getDefaultName();

      // Unwrap image values, only the url is needed by variables
      if (this.isImageValue(this.value)) {
        this.variableValue = this.value.url;
      } else {
        this.variableValue = this.value;
      }

      this.$nextTick(() => this.focusAndSelectInput());
    }
  }

  get variableLabels() {
    return globalStore.variables.map(item => item.label);
  }

  get collections() {
    return globalStore.variablesCollections;
  }

  get popoutWidth() {
    return DEFAULT_OXYGEN_POPOUT_WIDTH;
  }

  getInputForVariableType(type: VariableType) {
    return getInputForVariableType(type);
  }

  getDefaultName() {
    const name = this.name || this.type;

    const defaultName =
      name.charAt(0).toUpperCase() +
      name
        .slice(1)
        .replace(/family|_/g, "")
        .trim();

    return getUniqueNameWithSuffix(defaultName, this.variableLabels);
  }

  focusAndSelectInput() {
    const contentEl = this.$refs.content as HTMLElement;

    if (!contentEl) {
      return;
    }

    const input = contentEl.querySelector(`input`) as HTMLInputElement;

    setTimeout(() => {
      input?.focus();
      input?.select();
    }, 100);
  }

  setValue(value: string | undefined) {
    this.variableValue = value;
  }

  save() {
    const newVariable: Variable = {
      id: uuidv4(),
      cssVariableName: createVariableName(this.variableLabel),
      type: this.type,
      label: this.variableLabel,
      value: this.variableValue,
      collection: this.variableCollection || DEFAULT_VARIABLES_COLLECTION
    };

    addVariableToStoreAndSaveVariablesToWordPressIfVariablesPanelIsClosed(
      newVariable
    );

    this.variableLabel = "";
    this.variableValue = null;

    this.$emit("input", newVariable);
    this.$emit("close");
  }

  isImageValue(value: unknown): value is Media {
    const decoder = MediaV.decode;

    return pipe(
      value,
      decoder,
      fold(
        () => false,
        () => true
      )
    );
  }
}
